import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { QrCode, CheckCircle2, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

export default function ScanPage() {
  const readerRef = useRef(null);
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const extractId = (text) => {
    const m = (text || "").match(/\/deal\/([a-f0-9-]{6,})/i);
    return m ? m[1] : (text || "").trim();
  };

  const start = async () => {
    setError("");
    try {
      const html5Qr = new Html5Qrcode("bb-reader");
      scannerRef.current = html5Qr;
      await html5Qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          const id = extractId(decoded);
          if (!id) return;
          try {
            await html5Qr.stop(); setRunning(false);
          } catch (e) { /* ignore */ }
          try {
            const { data } = await api.post(`/markdowns/${id}/redeem`);
            setLast({ id, redemptions: data.redemptions });
            toast.success(`Redemption logged · ${data.redemptions} total`);
          } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Redeem failed"); }
        },
        () => { /* frame miss - ignore */ }
      );
      setRunning(true);
    } catch (e) {
      setError(e?.message || "Camera access denied");
    }
  };

  const stop = async () => {
    try { await scannerRef.current?.stop(); } catch (e) { /* ignore */ }
    setRunning(false);
  };

  useEffect(() => () => { stop(); }, []);

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={()=>navigate("/dashboard")} className="text-sm text-emerald-800 font-semibold inline-flex items-center gap-1 mb-3" data-testid="scan-back"><ArrowLeft className="w-4 h-4" />Back to dashboard</button>
      <Card className="border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-display text-2xl flex items-center gap-2"><QrCode className="w-6 h-6 text-emerald-800" />Scan a deal</CardTitle>
          <CardDescription>Point the camera at the shopper&apos;s QR — a redemption is auto-logged.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div id="bb-reader" ref={readerRef} className="w-full rounded-xl overflow-hidden bg-slate-900 min-h-[280px]" data-testid="scan-reader" />
          {!running ? (
            <Button onClick={start} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="scan-start"><Camera className="w-4 h-4 mr-2" />Start scanner</Button>
          ) : (
            <Button onClick={stop} variant="outline" className="w-full h-11" data-testid="scan-stop">Stop</Button>
          )}
          {error && <div className="text-sm text-red-600" data-testid="scan-error">{error}. Allow camera access and try again.</div>}
          {last && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2" data-testid="scan-last">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              <div className="text-sm text-emerald-900">Logged redemption · total <b>{last.redemptions}</b></div>
              <Button size="sm" variant="outline" onClick={start} className="ml-auto">Scan another</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
